import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Restaurant } from '../../types';
import { adminRestaurantsService } from '../../services/adminRestaurants';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { useRestaurantCache } from '../../context/RestaurantCacheContext';
import { useApp } from '../../context/AppContext';
import InlineAlert from '../../components/InlineAlert';
import TimezoneSelector from '../../components/TimezoneSelector';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';

// Componente para tarjeta de restaurante mejorada
const RestaurantCard = ({ 
  restaurant, 
  onEdit, 
  onSelect,
  onViewPublic,
  onToggleOpenClose,
  isSelected
}: {
  restaurant: Restaurant;
  onEdit: (restaurant: Restaurant) => void;
  onSelect: (restaurant: Restaurant) => void;
  onViewPublic: (restaurant: Restaurant) => void;
  onToggleOpenClose: (restaurant: Restaurant) => void;
  isSelected: boolean;
}) => {
  const isPlanLimited = restaurant.status === 'plan_limited' || (typeof restaurant.status === 'string' && restaurant.status.toLowerCase().includes('plan'));
  const { isAuthenticated, isOwner, isManager } = useAuth();
  const navigate = useNavigate();
  const canPromptPay = isPlanLimited && isAuthenticated && (isOwner() || isManager());

  return (
    <div onClick={() => onSelect(restaurant)} title="Seleccionar" className={`group relative bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border transition-all duration-300 transform hover:-translate-y-1 overflow-hidden h-[580px] flex flex-col cursor-pointer ${
      isSelected 
        ? 'border-[#22B6B2] shadow-[#22B6B2]/20 scale-105' 
        : 'border-[#22B6B2]/10 hover:shadow-xl hover:border-[#22B6B2]/20'
    }`}>
      {/* Imagen del restaurante */}
      <div className="relative h-52 bg-gradient-to-br from-[#22B6B2]/10 to-[#2C4358]/10">
        {restaurant.image ? (
          <img
            src={restaurant.image}
            alt={restaurant.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl text-[#85929e]">🏪</span>
          </div>
        )}
        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        
        {/* Badge de selección */}
        {isSelected && (
          <div className="absolute top-4 right-4 z-10">
            <div className="bg-[#22B6B2]/90 text-white px-3 py-1.5 rounded-full border-2 border-white shadow-lg backdrop-blur-sm font-bold text-sm">
              ✅ Seleccionado
            </div>
          </div>
        )}
        
        {/* Badge de estado */}
        <div className="absolute bottom-4 left-4 z-10">
          {isPlanLimited ? (
            <div className="backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/50 shadow-lg font-bold text-sm bg-red-600/90 text-white">
              Limitado por plan
            </div>
          ) : (
            <div className="backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/50 shadow-lg font-bold text-sm bg-white/90 text-[#2C4358] flex items-center space-x-2">
              <img
                src={restaurant.isOpen ? '/assets/images/puertaabierta.png' : '/assets/images/puertacerrada.png'}
                alt={restaurant.isOpen ? 'Abierto' : 'Cerrado'}
                className="w-6 h-6"
              />
              <span>{restaurant.isOpen ? 'Abierto' : 'Cerrado'}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className={`p-5 flex-1 flex flex-col ${isPlanLimited ? 'opacity-60 saturate-50 select-none' : ''}`}>
        {/* Header con nombre y acciones */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-[#2C4358] truncate mb-1">{restaurant.name}</h3>
            {restaurant.timezone?.name && (
              <div className="text-sm text-[#22B6B2] font-semibold truncate mb-1">{restaurant.timezone.name}</div>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#22B6B2] rounded-full animate-pulse"></div>
              <p className="text-sm font-medium text-[#22B6B2]">Restaurante activo</p>
            </div>
          </div>
          
          {/* Toggle abrir/cerrar */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleOpenClose(restaurant); }}
            className={`inline-flex items-center px-3 py-2 rounded-full border transition-all duration-300 font-bold text-xs hover:scale-105 focus:outline-none focus:ring-4 ${
              restaurant.isOpen
                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 focus:ring-green-200'
                : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 focus:ring-red-200'
            }`}
            title={restaurant.isOpen ? 'Cerrar restaurante' : 'Abrir restaurante'}
            aria-label={restaurant.isOpen ? 'Cerrar restaurante' : 'Abrir restaurante'}
          >
            {restaurant.isOpen ? (
              <img src="/assets/images/puertacerrada.png" alt="Cerrar" className="w-6 h-6 mr-2" />
            ) : (
              <img src="/assets/images/puertaabierta.png" alt="Abrir" className="w-6 h-6 mr-2" />
            )}
            <span>{restaurant.isOpen ? 'Cerrar' : 'Abrir'}</span>
          </button>
        </div>
        
        {/* Información del restaurante */}
        <div className="space-y-2 mb-3 min-h-[70px]">
          {restaurant.description ? (
            <p className="text-[#85929e] text-sm line-clamp-2 leading-relaxed">{restaurant.description}</p>
          ) : (
            <div className="h-8"></div>
          )}
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 text-xs text-[#85929e] bg-[#22B6B2]/5 px-2 py-1 rounded-lg border border-[#22B6B2]/20">
              <span>📍</span>
              <span>{restaurant.address || 'Sin dirección'}</span>
            </div>
          </div>
          
          {restaurant.phone ? (
            <div className="flex items-center space-x-1 text-xs text-[#85929e] bg-[#22B6B2]/5 px-2 py-1 rounded-lg border border-[#22B6B2]/20">
              <span>📞</span>
              <span>{restaurant.phone}</span>
            </div>
          ) : (
            <div className="h-6"></div>
          )}
        </div>
        
        {/* Botones de acción (la card completa selecciona) */}
        <div className="flex flex-col space-y-2 transition-all duration-300 mt-auto pt-3">
          <button
            onClick={(e) => { e.stopPropagation(); onViewPublic(restaurant); }}
            className="relative group overflow-hidden w-full py-3 px-4 rounded-xl transition-all duration-300 font-bold text-sm text-white bg-gradient-to-r from-[#22B6B2] to-[#2C4358] hover:from-[#1fa19e] hover:to-[#263a4a] hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30 shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            <span className="relative z-10 inline-flex items-center justify-center">
              <span className="mr-2 transition-transform duration-300 group-hover:scale-110">👁️</span>
              <span>Preview del menú</span>
              <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
            </span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(restaurant); }}
            className="relative group overflow-hidden w-full py-3 px-4 rounded-xl transition-all duration-300 font-bold text-sm border bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#f7ebd6]/30"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            <span className="relative z-10 inline-flex items-center justify-center">
              <span className="mr-2 transition-transform duration-300 group-hover:-rotate-6">✏️</span>
              <span>Editar</span>
            </span>
          </button>
        </div>
      </div>
      {canPromptPay && (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-md border border-[#22B6B2]/30 rounded-2xl shadow-2xl px-6 py-4 text-center">
            <div className="text-base font-bold text-[#2C4358] mb-2">Restaurante limitado por plan</div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white font-bold hover:opacity-90 transition"
            >
              Actualizar plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function RestaurantsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedRestaurantId, setSelectedRestaurant } = useSelectedRestaurant();
  const { dispatch } = useApp();
  const { refreshRestaurants } = useRestaurantCache();
  const { subscription, loading: subscriptionLoading } = useSubscriptionStatus();
  
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null);
  const [newRestaurantData, setNewRestaurantData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    cuisine: '',
    delivery_time: '',
    opening_time: '',
    closing_time: '',
    minimum_order: 0,
    delivery_fee: 0,
    is_open: true,
    accept_reservations: false,
    allow_public_menu_orders: false,
    reservations_info_url: '',
    image: '',
    timezone: { name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City', offset: -new Date().getTimezoneOffset() / 60 }
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState<number>(0);
  const [imageUploadStatus, setImageUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [pageAlert, setPageAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; text: string } | null>(null);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string; onRetry?: () => void }>({ open: false, message: '' });

  const canEnableOffsiteOrders = (() => {
    const raw = String(subscription?.metadata?.planName || '').toLowerCase().trim();
    const normalized = raw
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ú/g, 'u');
    return normalized === 'basico' || normalized === 'profesional' || normalized === 'enterprise';
  })();

  // Si el plan no permite pedidos off-site, forzar la opción a false cuando el modal está abierto
  useEffect(() => {
    if (!showAddModal) return;
    // En edición, NO forzar a false (evita resetear al guardar otros cambios).
    // El toggle ya está deshabilitado por plan, así que no se puede “activar” si no aplica.
    if (editingRestaurant) return;
    if (subscriptionLoading) return;
    if (canEnableOffsiteOrders) return;
    setNewRestaurantData((prev: any) => ({ ...prev, allow_public_menu_orders: false }));
  }, [showAddModal, editingRestaurant, subscriptionLoading, canEnableOffsiteOrders]);

  useEffect(() => {
    if (user?.uid) {
      loadRestaurants();
    }
  }, [user?.uid]);

  // Escuchar cambios de estado para actualizar íconos al vuelo
  useEffect(() => {
    const handler = (e: Event) => {
      const evt = e as CustomEvent<{ restaurantId: string; isOpen: boolean }>;
      setRestaurants(prev => prev.map(r => r.id === evt.detail.restaurantId ? { ...r, isOpen: evt.detail.isOpen } as Restaurant : r));
    };
    window.addEventListener('ots:restaurantStatusChanged', handler as EventListener);
    return () => window.removeEventListener('ots:restaurantStatusChanged', handler as EventListener);
  }, []);

  const loadRestaurants = async () => {
    try {
      if (!user?.uid) {
        console.log('⚠️ Usuario no autenticado, esperando...');
        return;
      }
      
      setLoading(true);
      console.log('🔄 Cargando restaurantes para usuario:', user.uid);
      const data = await adminRestaurantsService.getRestaurants(user.uid);
      setRestaurants(data as Restaurant[]);
    } catch (error) {
      console.error('Error loading restaurants:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setPageAlert({ type: 'error', text: error.message });
      } else {
        setPageAlert({ type: 'error', text: 'Error cargando restaurantes' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddRestaurant = () => {
    setEditingRestaurant(null);
    setNewRestaurantData({
      name: '',
      description: '',
      address: '',
      phone: '',
      cuisine: '',
      delivery_time: '',
      opening_time: '',
      closing_time: '',
      minimum_order: 0,
      delivery_fee: 0,
      is_open: true,
      accept_reservations: false,
      allow_public_menu_orders: false,
      reservations_info_url: '',
      image: '',
      timezone: { name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City', offset: -new Date().getTimezoneOffset() / 60 }
    });
    setSelectedImage(null);
    setImagePreview('');
    setShowAddModal(true);
    setPageAlert(null);
  };

  const handleEditRestaurant = (restaurant: Restaurant) => {
    setEditingRestaurant(restaurant);
    setNewRestaurantData({
      name: restaurant.name,
      description: restaurant.description || '',
      address: restaurant.address || '',
      phone: restaurant.phone || '',
      cuisine: Array.isArray(restaurant.cuisine) ? restaurant.cuisine[0] || '' : restaurant.cuisine || '',
      delivery_time: restaurant.delivery_time || restaurant.deliveryTime || '',
      opening_time: (restaurant as any).opening_time || restaurant.openingTime || '',
      closing_time: (restaurant as any).closing_time || restaurant.closingTime || '',
      minimum_order: 0, // No existe en el tipo Restaurant
      delivery_fee: 0, // No existe en el tipo Restaurant
      is_open: restaurant.isOpen !== undefined ? restaurant.isOpen : true,
      accept_reservations: (restaurant as any).acceptReservations === true,
      allow_public_menu_orders: (restaurant as any).allowPublicMenuOrders === true,
      reservations_info_url: (restaurant as any).reservationsInfoUrl || '',
      image: restaurant.image || '',
      timezone: (restaurant as any).timezone || { name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City', offset: -new Date().getTimezoneOffset() / 60 }
    });
    setSelectedImage(null);
    setImagePreview(restaurant.image || '');
    setShowAddModal(true);
    setPageAlert(null);
  };

  // Funciones para manejo de imágenes
  const handleSelectImages = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = false;
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        handleImageSelect(files[0]);
      }
    };
    input.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      handleImageSelect(files[0]);
    }
  };

  const handleCreateRestaurant = async () => {
    if (!user?.uid) {
      setPageAlert({ type: 'error', text: 'Error: Usuario no autenticado' });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Preparar datos del restaurante
      const restaurantData = {
        ...newRestaurantData,
        image: selectedImage ? selectedImage.name : undefined
      };

      // Crear el restaurante en el backend
      const response = await adminRestaurantsService.createRestaurant(user.uid, restaurantData);
      
      // Si hay imagen seleccionada y el backend devolvió una URL firmada, subir la imagen
      if (selectedImage && response && (response as any).image) {
        const signedUrl = (response as any).image;
        
        // Subir la imagen usando la URL firmada con progress tracking
        await uploadImageWithProgress(selectedImage, signedUrl);
      }

      setShowAddModal(false);
      // Refrescar listado global para que RestaurantSelector vea el nuevo restaurante
      await refreshRestaurants(user.uid, user.email || undefined);
      loadRestaurants();
      setPageAlert({ type: 'success', text: 'Restaurante creado correctamente' });
    } catch (error) {
      console.error('Error creating restaurant:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo crear el restaurante', message: error.message, details: undefined });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'Error al crear el restaurante. Por favor, intenta de nuevo.', details: String(error) });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleUpdateRestaurant = async () => {
    if (!editingRestaurant) return;
    if (!user?.uid) {
      setPageAlert({ type: 'error', text: 'Error: Usuario no autenticado' });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Preparar datos del restaurante
      const updateData = {
        ...newRestaurantData,
        image: selectedImage ? selectedImage.name : undefined,
        is_update_image: !!selectedImage
      };

      // Actualizar el restaurante en el backend
      const response = await adminRestaurantsService.updateRestaurant(
        user.uid, 
        editingRestaurant.id, 
        updateData
      );
      
      // Si hay imagen seleccionada y el backend devolvió una URL firmada, subir la imagen
      if (selectedImage && response && (response as any).image) {
        const signedUrl = (response as any).image;
        
        // Subir la imagen usando la URL firmada con progress tracking
        await uploadImageWithProgress(selectedImage, signedUrl);
      }

      setShowAddModal(false);
      // Refrescar listado global tras actualización
      await refreshRestaurants(user.uid, user.email || undefined);
      loadRestaurants();
      setPageAlert({ type: 'success', text: 'Restaurante actualizado correctamente' });
    } catch (error) {
      console.error('Error updating restaurant:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo actualizar', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'Error al actualizar el restaurante. Por favor, intenta de nuevo.', details: String(error) });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleToggleOpenClose = async (restaurant: Restaurant) => {
    // Reutilizar flujo del header: seleccionar restaurante y abrir el mismo modal de estado
    await setSelectedRestaurant(
      restaurant.id,
      restaurant.name,
      restaurant.image,
      restaurant.isOpen ? 'open' : 'closed',
      (restaurant as any).timezone || null
    );
    window.dispatchEvent(new Event('ots:openStatusModal'));
  };

  // Eliminar restaurante (ya no se usa en la UI, mantenido para referencia futura)
  // const handleDeleteRestaurant = async (restaurant: Restaurant) => { ... };

  const handleSelectRestaurant = async (restaurant: Restaurant) => {
    await setSelectedRestaurant(
      restaurant.id,
      restaurant.name,
      restaurant.image,
      restaurant.isOpen ? 'open' : 'closed',
      (restaurant as any).timezone || null
    );
  };

  const handleViewPublicMenu = async (restaurant: Restaurant) => {
    await setSelectedRestaurant(
      restaurant.id,
      restaurant.name,
      restaurant.image,
      restaurant.isOpen ? 'open' : 'closed',
      (restaurant as any).timezone || null
    );
            const url = `/menu?restaurantId=${restaurant.id}`;
    navigate(url);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingRestaurant(null);
    setSelectedImage(null);
    setImagePreview('');
    setImageUploadProgress(0);
    setImageUploadStatus('idle');
  };

  // Función para subir imagen con progress tracking
  const uploadImageWithProgress = async (file: File, signedUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Configurar progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setImageUploadProgress(progress);
        }
      });
      
      // Configurar eventos de respuesta
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setImageUploadStatus('success');
          resolve();
        } else {
          setImageUploadStatus('error');
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        setImageUploadStatus('error');
        reject(new Error('Network error'));
      });
      
      // Iniciar upload
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      setImageUploadStatus('uploading');
      setImageUploadProgress(0);
      xhr.send(file);
    });
  };

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    setImageUploadProgress(0);
    setImageUploadStatus('idle');
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-gray-100 max-w-md w-full">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">🔒</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Acceso denegado</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Necesitas iniciar sesión para acceder a esta página
          </p>
          <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
            <p className="text-sm text-red-800 font-medium">
              💡 Inicia sesión con tu cuenta de administrador
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!user.uid) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-gray-100 max-w-md w-full">
          <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⏳</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Cargando...</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Verificando tu autenticación
          </p>
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <p className="text-sm text-blue-800 font-medium">
              💡 Espera un momento mientras cargamos tu información
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header principal minimalista */}
        <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-lg border border-[#22B6B2]/10 p-8 mb-8">
          {pageAlert && (
            <div className="mb-4">
              <InlineAlert type={pageAlert.type} message={pageAlert.text} onClose={() => setPageAlert(null)} />
            </div>
          )}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
            <button
              onClick={handleAddRestaurant}
              className="inline-flex items-center px-8 py-4 bg-[#f7ebd6] text-[#2C4358] font-bold rounded-2xl hover:bg-[#f0e0c0] focus:outline-none focus:ring-4 focus:ring-[#f7ebd6]/30 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duración-1000"></div>
              <span className="relative z-10 mr-3 text-xl group-hover:rotate-90 transition-transform duration-300">+</span>
              <span className="relative z-10">Agregar Restaurante</span>
            </button>
            
            {/* Estadísticas */}
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-[#22B6B2]/20">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2C4358] mb-1">{restaurants.length}</div>
                <div className="text-sm text-[#85929e] font-medium">Restaurantes</div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-6"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
              </div>
              <h3 className="text-lg font-bold text-[#2C4358] mb-2">Cargando restaurantes...</h3>
              <p className="text-[#85929e]">Preparando todo para ti</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {restaurants.map((restaurant) => (
              <RestaurantCard
                key={restaurant.id}
                restaurant={restaurant}
                 onEdit={handleEditRestaurant}
                onSelect={handleSelectRestaurant}
                onViewPublic={handleViewPublicMenu}
                 onToggleOpenClose={handleToggleOpenClose}
                isSelected={selectedRestaurantId === restaurant.id}
              />
            ))}
            {restaurants.length === 0 && (
              <div className="col-span-full">
                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-12 text-center shadow-lg border border-[#22B6B2]/10">
                  <div className="w-20 h-20 bg-[#22B6B2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-4xl">🏪</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#2C4358] mb-3">No hay restaurantes</h3>
                  <p className="text-[#85929e] mb-8 max-w-md mx-auto text-lg leading-relaxed">
                    Comienza creando tu primer restaurante para gestionar menús, mesas y pedidos
                  </p>
                  <button
                    onClick={handleAddRestaurant}
                    className="inline-flex items-center px-8 py-4 bg-[#f7ebd6] text-[#2C4358] font-bold rounded-2xl hover:bg-[#f0e0c0] transition-all duration-300 transform hover:scale-105 shadow-lg relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    <span className="relative z-10 mr-3 text-xl group-hover:rotate-90 transition-transform duration-300">+</span>
                    <span className="relative z-10">Crear Primer Restaurante</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal mejorado */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-2 p-0 relative max-h-[95vh] flex flex-col border border-white/20">
            {/* Fondo animado */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
            
            {/* Header fijo */}
            <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-6 sm:px-8 py-6 rounded-t-3xl">
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md hover:bg-white flex items-center justify-center text-[#2C4358] hover:text-[#22B6B2] text-2xl transition-all duration-300 z-20 group shadow-lg"
                aria-label="Cerrar"
              >
                <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
              </button>
              <div className="text-center">
                <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#2C4358] mb-2 tracking-tight">
                  {editingRestaurant ? 'Editar' : 'Crear'} Restaurante
                </h2>
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-2 h-2 bg-[#22B6B2] rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-[#2C4358] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-[#85929e] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
            
            {/* Body scrolleable */}
            <div className="overflow-hidden rounded-b-3xl flex-1 flex flex-col">
              <form onSubmit={e => { e.preventDefault(); editingRestaurant ? handleUpdateRestaurant() : handleCreateRestaurant(); }} className="relative z-10 p-8 space-y-8 overflow-y-auto h-full">
                {/* Inline alert dentro del modal si hay errores */}
                {pageAlert && (
                  <InlineAlert type={pageAlert.type} message={pageAlert.text} onClose={() => setPageAlert(null)} />
                )}
                
                {/* Nombre */}
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">
                    Nombre del restaurante *
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={newRestaurantData.name}
                    onChange={e => setNewRestaurantData({ ...newRestaurantData, name: e.target.value })}
                    className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                    placeholder="Ej: Mi Restaurante"
                    required
                  />
                  {!newRestaurantData.name && (
                    <div className="mt-2 flex items-center space-x-2 text-[#22B6B2] font-semibold animate-pulse">
                      <span className="text-xl">⚠️</span>
                      <span>El nombre es obligatorio</span>
                    </div>
                  )}
                </div>

                {/* Descripción */}
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">
                    Descripción
                  </label>
                  <textarea
                    value={newRestaurantData.description}
                    onChange={e => setNewRestaurantData({ ...newRestaurantData, description: e.target.value })}
                    className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl resize-none"
                    placeholder="Describe tu restaurante..."
                    rows={3}
                  />
                </div>

                {/* Dirección */}
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={newRestaurantData.address}
                    onChange={e => setNewRestaurantData({ ...newRestaurantData, address: e.target.value })}
                    className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                    placeholder="Ej: Calle Principal 123"
                  />
                </div>

                {/* Teléfono + Zona horaria */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={newRestaurantData.phone}
                      onChange={e => setNewRestaurantData({ ...newRestaurantData, phone: e.target.value })}
                      className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                      placeholder="Ej: +1 234 567 8900"
                      required
                    />
                  </div>

                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">
                      Zona horaria
                    </label>
                    <TimezoneSelector
                      value={newRestaurantData.timezone}
                      onChange={(tz) => setNewRestaurantData({ ...newRestaurantData, timezone: tz })}
                    />
                  </div>
                </div>

                {/* Horario operativo (define la “jornada” de órdenes) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">
                      Hora de apertura (jornada)
                    </label>
                    <input
                      type="time"
                      value={(newRestaurantData as any).opening_time || ''}
                      onChange={e => setNewRestaurantData({ ...newRestaurantData, opening_time: e.target.value })}
                      className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                    />
                    <p className="text-sm text-[#85929e] mt-2">
                      Las órdenes se consultan desde esta hora hasta antes de esta misma hora del día siguiente.
                    </p>
                  </div>

                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">
                      Hora de cierre (opcional)
                    </label>
                    <input
                      type="time"
                      value={(newRestaurantData as any).closing_time || ''}
                      onChange={e => setNewRestaurantData({ ...newRestaurantData, closing_time: e.target.value })}
                      className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                    />
                    <p className="text-sm text-[#85929e] mt-2">
                      Solo informativo por ahora (no afecta el listado).
                    </p>
                  </div>
                </div>

                {/* Cocina + Tiempo de entrega */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">
                      Tipo de Cocina *
                    </label>
                    <input
                      type="text"
                      value={newRestaurantData.cuisine}
                      onChange={e => setNewRestaurantData({ ...newRestaurantData, cuisine: e.target.value })}
                      className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                      placeholder="Ej: Italiana, Mexicana, Asiática..."
                      required
                    />
                  </div>

                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">
                      Tiempo de Entrega *
                    </label>
                    <input
                      type="text"
                      value={newRestaurantData.delivery_time}
                      onChange={e => setNewRestaurantData({ ...newRestaurantData, delivery_time: e.target.value })}
                      className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                      placeholder="Ej: 30-45 minutos"
                      required
                    />
                  </div>
                </div>

            {/* Reservaciones */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="group">
                <label className="block text-lg font-bold text-[#2C4358] mb-3">Aceptar reservaciones</label>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={newRestaurantData.accept_reservations}
                    onChange={(e) => setNewRestaurantData({ ...newRestaurantData, accept_reservations: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-[#2C4358] font-semibold">Habilitar desde Admin</span>
                </div>
                <p className="text-sm text-[#85929e] mt-2">Para que los clientes puedan reservar, activa esta opción.</p>
              </div>
              <div className="group">
                <label className="block text-lg font-bold text-[#2C4358] mb-3">URL info reservaciones (opcional)</label>
                <input
                  type="text"
                  value={newRestaurantData.reservations_info_url}
                  onChange={(e) => setNewRestaurantData({ ...newRestaurantData, reservations_info_url: e.target.value })}
                  placeholder="https://tu-sitio.com/reservaciones"
                  className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                />
                <p className="text-sm text-[#85929e] mt-2">Enlace con políticas o detalles de tus reservaciones.</p>
              </div>
            </div>

            {/* Pedidos off-site */}
            <div className="group">
              <label className="block text-lg font-bold text-[#2C4358] mb-3">Habilitar pedidos off‑site</label>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={newRestaurantData.allow_public_menu_orders}
                  onChange={(e) => setNewRestaurantData({ ...newRestaurantData, allow_public_menu_orders: e.target.checked })}
                  disabled={subscriptionLoading || !canEnableOffsiteOrders}
                  className="w-5 h-5 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="text-[#2C4358] font-semibold">Permitir pedidos directos desde el menú digital</span>
              </div>
              <p className="text-sm text-[#85929e] mt-2">
                Al activarlo, tus clientes podrán hacer un pedido directamente desde tu menú digital (por ejemplo, desde tu sitio web o redes sociales) y el pedido se recibirá automáticamente en el sistema.
              </p>
              {!subscriptionLoading && !canEnableOffsiteOrders && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <div className="font-bold">Disponible en plan Básico o superior.</div>
                  <div className="mt-1">
                    <button
                      type="button"
                      onClick={() => navigate('/pricing')}
                      className="font-semibold underline text-amber-900 hover:opacity-80"
                    >
                      Ver planes
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Imagen */}
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">
                    Imagen del restaurante
                  </label>
                  
                  {/* Área de drag & drop */}
                  <div
                    className={`relative w-full min-h-[200px] p-6 border-2 border-dashed rounded-2xl transition-all duration-300 overflow-hidden
                      ${isDragging 
                        ? 'border-[#22B6B2] bg-[#22B6B2]/5 scale-105' 
                        : 'border-[#22B6B2]/30 bg-white hover:border-[#22B6B2] hover:bg-white'
                      }
                    `}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleSelectImages}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#22B6B2]/5 to-[#2C4358]/5 animate-pulse"></div>
                    
                    {selectedImage || imagePreview ? (
                      /* Preview de imagen seleccionada */
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold text-[#2C4358] flex items-center space-x-2">
                            <span className="text-2xl">🖼️</span>
                            <span>Imagen seleccionada</span>
                          </h4>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImage(null);
                              setImagePreview('');
                              setImageUploadProgress(0);
                              setImageUploadStatus('idle');
                            }}
                            className="px-4 py-2 bg-[#f7ebd6] text-[#2C4358] rounded-xl hover:bg-[#f0e0c0] transition-all duration-300 font-semibold border border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
                          >
                            Cambiar imagen
                          </button>
                        </div>
                        
                        <div className="relative">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-xl border-2 border-[#22B6B2]/20"
                          />
                          {selectedImage && (
                            <div className="absolute top-2 right-2 bg-[#22B6B2]/90 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm">
                              Nuevo
                            </div>
                          )}
                          
                          {/* Progress Bar */}
                          {imageUploadStatus === 'uploading' && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm p-3 rounded-b-xl">
                              <div className="flex items-center justify-between text-white text-sm mb-2">
                                <span>📤 Subiendo imagen...</span>
                                <span>{imageUploadProgress}%</span>
                              </div>
                              <div className="w-full bg-white/20 rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-[#22B6B2] to-[#2C4358] h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${imageUploadProgress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {/* Status Indicators */}
                          {imageUploadStatus === 'success' && (
                            <div className="absolute top-2 left-2 bg-green-500/90 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm">
                              ✅ Subida exitosa
                            </div>
                          )}
                          
                          {imageUploadStatus === 'error' && (
                            <div className="absolute top-2 left-2 bg-red-500/90 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm">
                              ❌ Error al subir
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Estado vacío */
                      <div className="relative z-10 text-center py-12">
                        <div className={`text-5xl mb-4 transition-transform duration-300 ${isDragging ? 'scale-125' : 'group-hover:scale-110'}`}>
                          {isDragging ? '📁' : '📸'}
                        </div>
                        <h3 className="text-xl font-bold text-[#2C4358] mb-2">
                          {isDragging ? '¡Suelta la imagen aquí!' : 'Arrastra y suelta una imagen aquí'}
                        </h3>
                        <p className="text-[#85929e]">
                          {isDragging ? 'Suelta para agregar la imagen' : 'O haz clic para seleccionar archivo'}
                        </p>
                        <p className="text-sm text-[#85929e] mt-2">
                          Soporta JPG, PNG, WebP (máx. 5MB)
                        </p>
                      </div>
                    )}
                    
                    {/* Overlay de instrucciones cuando hay imagen */}
                    {selectedImage && (
                      <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-xl border border-white/20">
                        <div className="flex items-center justify-center space-x-2">
                          <span>📸</span>
                          <span>Arrastra otra imagen aquí para cambiar</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/5 to-[#2C4358]/5 backdrop-blur-md border-t border-[#22B6B2]/20 px-6 sm:px-8 py-4 rounded-b-3xl">
              <div className="flex flex-col sm:flex-row gap-4 justify-end">
                <button
                  onClick={handleCloseModal}
                  className="px-8 py-4 bg-white border-2 border-[#85929e]/30 text-[#2C4358] font-bold rounded-2xl hover:bg-[#85929e]/5 hover:border-[#85929e] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingRestaurant ? handleUpdateRestaurant : handleCreateRestaurant}
                  disabled={!newRestaurantData.name || !newRestaurantData.cuisine || !newRestaurantData.delivery_time}
                  className="px-8 py-4 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white font-bold rounded-2xl hover:from-[#1a8f8c] hover:to-[#1e3a5f] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <span className="relative z-10 flex items-center space-x-2">
                    <span>{editingRestaurant ? '💾' : '✨'}</span>
                    <span>{editingRestaurant ? 'Actualizar' : 'Crear'} Restaurante</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Error Modal global */}
      <ErrorModal
        isOpen={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onClose={() => setErrorModal({ open: false, message: '' })}
      />
    </div>
  );
} 